import { useState } from "react";
import { communityPosts } from "@/mocks/communication";

const tagColorMap: Record<string, string> = {
  teal: "bg-teal-100 text-teal-700",
  rose: "bg-rose-100 text-rose-600",
  amber: "bg-amber-100 text-amber-700",
  green: "bg-green-100 text-green-700",
};

const helpTypeBg: Record<string, string> = {
  "Free Medical Advice": "bg-teal-50 text-teal-600 border-teal-100",
  "Needs Support": "bg-rose-50 text-rose-600 border-rose-100",
  "Free Consultation": "bg-teal-50 text-teal-600 border-teal-100",
  "Donated": "bg-amber-50 text-amber-600 border-amber-100",
  "Free Counseling": "bg-teal-50 text-teal-600 border-teal-100",
  "Free Meals": "bg-green-50 text-green-600 border-green-100",
};

type Post = typeof communityPosts[0] & { whatsapp?: string };

interface Props {
  post: Post;
  onDonate: () => void;
  isLoggedIn?: boolean;
  onLoginRequired?: () => void;
}

export default function CommunityPostCard({ post, onDonate, isLoggedIn, onLoginRequired }: Props) {
  const [liked, setLiked] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyCount, setReplyCount] = useState(post.replies);
  const [replySent, setReplySent] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  const handleReply = () => {
    if (!replyText.trim()) return;
    setReplyCount((c) => c + 1);
    setReplyText("");
    setReplySent(true);
    setShowReply(false);
    setTimeout(() => setReplySent(false), 3000);
  };

  const handleWhatsAppClick = () => {
    if (!isLoggedIn) {
      onLoginRequired?.();
      return;
    }
    setShowWhatsApp((v) => !v);
  };

  const whatsappNumber = post.whatsapp || "+1 (555) 012-3456";

  return (
    <div className="bg-white border border-stone-100 rounded-2xl p-5 hover:border-rose-100 transition-colors flex flex-col">
      {/* User header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
          <img src={post.avatar} alt={post.user} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-gray-900 text-sm">{post.user}</span>
            <span className="text-base">{post.country}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <span className="text-stone-400 text-xs">{post.role}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tagColorMap[post.tagColor] || "bg-stone-100 text-stone-600"}`}>
              {post.tag}
            </span>
          </div>
        </div>
        <span className="text-stone-400 text-xs flex-shrink-0">{post.time}</span>
      </div>

      {/* Emotion + help type */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="flex items-center gap-1 text-xs text-stone-500">
          <i className={`${post.emotionIcon} text-rose-400`}></i>
          Feeling {post.emotion}
        </span>
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${helpTypeBg[post.helpType] || "bg-stone-50 text-stone-500 border-stone-100"}`}>
          {post.helpType}
        </span>
      </div>

      {/* Content */}
      <p className="text-stone-700 text-sm leading-relaxed mb-4 flex-1">{post.content}</p>

      {/* WhatsApp reveal */}
      {showWhatsApp && (
        <div className="mb-3 bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
            <i className="ri-whatsapp-line text-white text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-green-700 font-semibold mb-0.5">WhatsApp</p>
            <p className="text-green-800 font-bold text-sm">{whatsappNumber}</p>
          </div>
          <a
            href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="px-3 py-1.5 bg-green-500 hover:bg-green-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Chat Now
          </a>
        </div>
      )}

      {/* Reply input */}
      {showReply && (
        <div className="mb-3 flex gap-2">
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a helpful reply..."
            className="flex-1 border border-stone-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-teal-400"
            onKeyDown={(e) => e.key === "Enter" && handleReply()}
          />
          <button
            onClick={handleReply}
            className="px-3 py-1.5 bg-teal-500 text-white text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap"
          >
            Send
          </button>
        </div>
      )}
      {replySent && (
        <p className="text-teal-500 text-xs mb-2 flex items-center gap-1">
          <i className="ri-checkbox-circle-line"></i> Reply sent! Thank you for helping.
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-50 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLiked((v) => !v)}
            className={`flex items-center gap-1 text-xs font-semibold transition-colors cursor-pointer ${liked ? "text-rose-500" : "text-stone-400 hover:text-rose-400"}`}
          >
            <i className={liked ? "ri-heart-fill" : "ri-heart-line"}></i>
            {post.likes + (liked ? 1 : 0)}
          </button>
          <button
            onClick={() => setShowReply((v) => !v)}
            className="flex items-center gap-1 text-xs font-semibold text-stone-400 hover:text-teal-500 transition-colors cursor-pointer"
          >
            <i className="ri-chat-3-line"></i>
            {replyCount}
          </button>
          {/* WhatsApp exchange button */}
          <button
            onClick={handleWhatsAppClick}
            className={`flex items-center gap-1 text-xs font-semibold transition-colors cursor-pointer ${showWhatsApp ? "text-green-600" : "text-stone-400 hover:text-green-500"}`}
            title="Exchange WhatsApp"
          >
            <i className="ri-whatsapp-line"></i>
            <span className="hidden sm:inline">WhatsApp</span>
          </button>
        </div>
        {post.tag === "Need Help" ? (
          <button
            onClick={() => setShowReply(true)}
            className="px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-hand-heart-line mr-1"></i>Help for Free
          </button>
        ) : (
          <button
            onClick={onDonate}
            className="px-3 py-1.5 bg-rose-500 hover:bg-rose-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-heart-fill mr-1"></i>Donate
          </button>
        )}
      </div>
    </div>
  );
}
